import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSelfKnowledge } from "../../Services/explore";
import DomainAssessmentView from "../../Components/YourElements/DomainAssessmentView";
import "../../Components/YourElements/styles.css";
import { t, DISCOVERY_TITLE, DISCOVERY_STATUS_LABEL } from "./copy";
import { resolveLifeArea, useLifeAreas } from "./lifeAreas";
import {
  AreaCardsSection,
  AreaHero,
  ExploreBackHeader,
  ExploreSubpageCard,
  TalkWithArisFooter,
  useStandaloneExploreHeader,
} from "./areaShared";
import "./style.css";
import "./selfKnowledge.css";

/**
 * Self-Knowledge — the one specialised Life Area, served by
 * `GET /api/v4/explore/self-knowledge` (aris-ai `self_knowledge_area.py`).
 *
 * The payload is `{ display_area, specialised, foundation_complete, hero,
 * sections }` with sections keyed by `key`, in a server-decided order:
 * CONTINUE_YOUR_DISCOVERIES (swapped for SELF_KNOWLEDGE_COMPLETE once all five
 * Discoveries are done), EXPLORE_YOUR_PERSONAL_MODEL (the Periodic Table
 * feature card), RECOMMENDED_FOR_YOU (omitted when empty) and TALK_WITH_ARIS.
 * Rendering walks the sections in the order they arrive.
 *
 * Discovery rows carry `{ discovery_key, order, domain, status,
 * estimated_minutes, available, complete }` — no member-facing title — so the
 * titles come from the local copy map. An AVAILABLE (or ACTIVE) row opens
 * /journey/discoveries; the Periodic Table card opens /elements (the
 * production Periodic Table of the Self page).
 */

const SECTION_KEY = {
  CONTINUE_YOUR_DISCOVERIES: "CONTINUE_YOUR_DISCOVERIES",
  SELF_KNOWLEDGE_COMPLETE: "SELF_KNOWLEDGE_COMPLETE",
  EXPLORE_YOUR_PERSONAL_MODEL: "EXPLORE_YOUR_PERSONAL_MODEL",
  RECOMMENDED_FOR_YOU: "RECOMMENDED_FOR_YOU",
  TALK_WITH_ARIS: "TALK_WITH_ARIS",
};

const discoveryTitle = (item) =>
  DISCOVERY_TITLE[item.discovery_key] ||
  String(item.discovery_key || "")
    .replace(/^D\d+_/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b./g, (ch) => ch.toUpperCase());

const domainLabel = (domain) => {
  if (!domain) return "";
  const pretty = String(domain)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b./g, (ch) => ch.toUpperCase());
  return `${pretty} Domain`;
};

function DiscoveryRow({ item, onOpen, onLocked }) {
  const available = item.available === true;
  const openable = available || item.complete === true;
  const label =
    DISCOVERY_STATUS_LABEL[item.status] ||
    String(item.status || "").replace(/_/g, " ");
  const chipModifier = item.complete
    ? "done"
    : available
    ? "next"
    : "locked";

  // Every row answers its tap: completed opens the results, available opens
  // the runner, locked explains the unlock order.
  return (
    <button
      type="button"
      className={`ex-sk-disc${openable ? "" : " ex-sk-disc--inert"}`}
      onClick={openable ? onOpen : onLocked}
    >
      <span className="ex-sk-disc__order">{item.order}</span>
      <span className="ex-sk-disc__text">
        <span className="ex-sk-disc__title">{discoveryTitle(item)}</span>
        <span className="ex-sk-disc__meta">
          {domainLabel(item.domain)}
          {item.estimated_minutes ? ` · ${item.estimated_minutes} min` : ""}
        </span>
      </span>
      <span className={`ex-sk-disc__chip ex-sk-disc__chip--${chipModifier}`}>
        {label}
      </span>
    </button>
  );
}

function SelfKnowledge() {
  useLifeAreas(); // re-render once the Life Areas arrive
  const navigate = useNavigate();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // The locked Discovery the member tapped — renders the unlock-order
  // explainer, or null when closed.
  const [lockedItem, setLockedItem] = useState(null);
  // The available Discovery the member wants to take — Discoveries run in the
  // app, so this renders the continue-on-the-app dialog.
  const [downloadItem, setDownloadItem] = useState(null);

  useStandaloneExploreHeader();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSelfKnowledge();
        if (!cancelled) setPayload(data || null);
      } catch (e) {
        console.error("Failed to load Self-Knowledge", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const area = resolveLifeArea({ key: "SELF_KNOWLEDGE" });
  const openDialogue = (card) =>
    card?.dialogue_id ? navigate(`/explore/dialogue/${card.dialogue_id}`) : null;
  // The member's personal model is the production Periodic Table page — not a
  // separate explorer.
  const openElements = () => navigate("/elements");
  // A completed Discovery shows its results on the Periodic Table; taking a
  // new one happens in the app, so an available row opens the download dialog.
  const openDiscovery = (item) =>
    item?.complete ? navigate("/elements") : setDownloadItem(item);

  // The one Discovery the member can take right now (drives the locked-row CTA).
  const discoveries =
    (payload?.sections || []).find(
      (section) => section?.key === SECTION_KEY.CONTINUE_YOUR_DISCOVERIES
    )?.discoveries || [];
  const nextAvailable = discoveries.find((d) => d.available === true) || null;

  const renderSection = (section, index) => {
    const key = `${section?.key || "unknown"}-${index}`;
    switch (section?.key) {
      case SECTION_KEY.CONTINUE_YOUR_DISCOVERIES:
        return (
          <section key={key} className="ex-section">
            <h2 className="ex-la-eyebrow">
              {t(section.title_key, "Continue Your Discoveries")}
            </h2>
            <div className="ex-stack">
              {(section.discoveries || []).map((item, i) => (
                <DiscoveryRow
                  key={item.discovery_key || i}
                  item={item}
                  onOpen={() => openDiscovery(item)}
                  onLocked={() => setLockedItem(item)}
                />
              ))}
            </div>
          </section>
        );

      case SECTION_KEY.SELF_KNOWLEDGE_COMPLETE:
        return (
          <section key={key} className="ex-sk-complete">
            <h2 className="ex-sk-complete__title">
              {t(section.title_key, "Self-Knowledge Complete")}
            </h2>
            <p className="ex-sk-complete__body">
              {t(
                section.body_key,
                "You've completed all five Discoveries. Your Periodic Table of the Self™ is now the primary way to explore who you are."
              )}
            </p>
            <button
              type="button"
              className="ex-la-talk__btn"
              onClick={openElements}
            >
              {t(section.cta_key, "View Your Elements")}
            </button>
          </section>
        );

      case SECTION_KEY.EXPLORE_YOUR_PERSONAL_MODEL:
        return (
          <button
            key={key}
            type="button"
            className="ex-sk-model"
            onClick={openElements}
          >
            <span className="ex-sk-model__title">
              {t(section.title_key, "Explore Your Elements")}
            </span>
            <span className="ex-sk-model__desc">
              {t(
                section.description_key,
                "Discover your strengths, natural tendencies, and what makes you uniquely you."
              )}
            </span>
            <span className="ex-sk-model__cta">
              {t(section.cta_key, "View Your Elements")}
            </span>
          </button>
        );

      case SECTION_KEY.RECOMMENDED_FOR_YOU:
        return (
          <AreaCardsSection
            key={key}
            section={section}
            titleFallback="Recommended for You"
            onOpenDialogue={openDialogue}
          />
        );

      case SECTION_KEY.TALK_WITH_ARIS:
        return (
          <TalkWithArisFooter
            key={key}
            section={section}
            onTalkWithAris={() => navigate("/dashboard")}
          />
        );

      default:
        // A section this client does not know yet is skipped, not broken on.
        return null;
    }
  };

  return (
    <div className="explore-page">
      <div className="explore-page__inner">
        <ExploreBackHeader to="/explore" />
        <ExploreSubpageCard>

          <AreaHero
            area={area}
            title={area.title}
            description={t(payload?.hero?.description_key, area.description)}
          />

          {loading && <div className="explore-card explore-card--status">Loading…</div>}

          {!loading && error && (
            <div className="explore-card explore-card--status">
              We couldn&apos;t load Self-Knowledge. Please try again.
            </div>
          )}

          {!loading && !error && (payload?.sections || []).map(renderSection)}
        </ExploreSubpageCard>
      </div>

      {downloadItem && (
        <div
          className="ex-sk-lock-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setDownloadItem(null)}
        >
          <div
            className="ex-sk-lock-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ex-sk-lock-close"
              aria-label="Close"
              onClick={() => setDownloadItem(null)}
            >
              ×
            </button>
            <DomainAssessmentView
              item={{ title: discoveryTitle(downloadItem) }}
            />
          </div>
        </div>
      )}

      {lockedItem && (
        <div
          className="ex-sk-lock-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setLockedItem(null)}
        >
          <div
            className="ex-sk-lock-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ex-sk-lock-close"
              aria-label="Close"
              onClick={() => setLockedItem(null)}
            >
              ×
            </button>
            <h2 className="ex-sk-lock-title">
              {lockedItem.status === "EMERGES"
                ? `${discoveryTitle(lockedItem)} emerges on its own`
                : "Discoveries unlock in order"}
            </h2>
            <p className="ex-sk-lock-body">
              {lockedItem.status === "EMERGES"
                ? "This one isn't taken directly — it comes together automatically once your other Discoveries are complete."
                : nextAvailable
                ? `Finish “${discoveryTitle(nextAvailable)}” to unlock “${discoveryTitle(
                    lockedItem
                  )}”.`
                : `“${discoveryTitle(lockedItem)}” unlocks after the Discoveries before it.`}
            </p>
            {nextAvailable && lockedItem.status !== "EMERGES" && (
              <button
                type="button"
                className="ex-la-talk__btn ex-sk-lock-cta"
                onClick={() => {
                  setLockedItem(null);
                  setDownloadItem(nextAvailable);
                }}
              >
                Continue: {discoveryTitle(nextAvailable)}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SelfKnowledge;
